import React, { useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { FaCircleXmark } from "react-icons/fa6";
import BeatLoader from "react-spinners/BeatLoader";
import { Button, Input } from "../../../Components/CustomComponents";
import ModalContainer from "../../../Components/ModalContainer";
import cssStyles from "../../../Components/Modals/styles/editModal.module.scss";
import { campaignLabel } from "../../Dashboard/constants/labels";
import styles from "../styles/campaignNotifications.module.scss";
import NotificationReadOnlyMeta from "./NotificationReadOnlyMeta";

const TITLE_MAX = 150;
const MESSAGE_MAX = 500;

const sliceValue = (event, max) =>
  String(event?.target?.value ?? "").slice(0, max);

export default function EditNotificationCopyModal({
  campaign,
  onClose,
  onSave,
  isSaving,
}) {
  const detail = campaign?.notification_detail || {};
  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: detail.title || "",
      message: detail.message || "",
    },
  });

  useEffect(() => {
    reset({
      title: detail.title || "",
      message: detail.message || "",
    });
  }, [detail.message, detail.title, reset]);

  const titleValue = watch("title") || "";
  const messageValue = watch("message") || "";

  const handleFormSubmit = (formData) => {
    onSave({
      title: String(formData.title || "").trim(),
      message: String(formData.message || "").trim(),
    });
  };

  return (
    <ModalContainer handleClose={onClose}>
      <div className={`${cssStyles.modalContainer} ${styles.editCopyModal}`}>
        <div className="d-flex align-items-center w-100 justify-content-between mb-4">
          <p className="fs-4 fw-medium m-0 black-text">Edit notification copy</p>
          <span className="bg-dark bg-opacity-75 d-flex p-3 rounded-5">
            <FaCircleXmark
              role="button"
              onClick={onClose}
              style={{ cursor: "pointer" }}
            />
          </span>
        </div>
        <div className="w-100">
          <p className="text-body-tertiary">
            {campaignLabel(campaign?.slug, campaign?.name)}
          </p>
          <NotificationReadOnlyMeta detail={detail} />
          <Form onSubmit={handleSubmit(handleFormSubmit)}>
            <Row>
              <Col md={12}>
                <p className="m-0 black-text">Title</p>
                <Controller
                  control={control}
                  name="title"
                  rules={{
                    required: "Title is required.",
                    maxLength: {
                      value: TITLE_MAX,
                      message: `Title must be ${TITLE_MAX} characters or fewer.`,
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="text"
                      value={value ?? ""}
                      onChange={(event) =>
                        onChange(sliceValue(event, TITLE_MAX))
                      }
                    />
                  )}
                />
                <div className={styles.counter}>
                  {titleValue.length}/{TITLE_MAX}
                </div>
                {errors.title ? (
                  <p className={styles.fieldError}>{errors.title.message}</p>
                ) : null}
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <p className="m-0 black-text">Message</p>
                <Controller
                  control={control}
                  name="message"
                  rules={{
                    required: "Message is required.",
                    maxLength: {
                      value: MESSAGE_MAX,
                      message: `Message must be ${MESSAGE_MAX} characters or fewer.`,
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      type="textarea"
                      as="textarea"
                      rows={5}
                      value={value ?? ""}
                      onChange={(event) =>
                        onChange(sliceValue(event, MESSAGE_MAX))
                      }
                    />
                  )}
                />
                <div className={styles.counter}>
                  {messageValue.length}/{MESSAGE_MAX}
                </div>
                {errors.message ? (
                  <p className={styles.fieldError}>{errors.message.message}</p>
                ) : null}
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Button
                  classes={`${cssStyles.cardBtn} ${styles.saveBtn} mt-2 ${
                    isSaving ? "bg-dark-subtle" : ""
                  }`}
                  text={isSaving ? <BeatLoader size={10} /> : "Save copy"}
                  type="Submit"
                  disabled={isSaving}
                />
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </ModalContainer>
  );
}
